import { createServerFn, createMiddleware } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { attachSupabaseAuth } from "@/integrations/supabase/auth-attacher";
import type { Database } from "@/integrations/supabase/types";
import { sendEmail, renderBookingRequestEmail, renderContactMessageEmail } from "@/lib/email.server";
import { renderBookingPdf } from "@/lib/booking-pdf.server";

const ADMIN_EMAIL = "clients@vortio.com";

// attachSupabaseAuth (client-side: attaches the bearer token) is scoped to just
// the admin-only functions below instead of being a global functionMiddleware
// in src/start.ts, so the Supabase Auth client (GoTrueClient, ~50KB+) is only
// loaded in the browser on /admin routes, not on every public page.
const requireAdmin = createMiddleware({ type: "function" })
  .middleware([attachSupabaseAuth, requireSupabaseAuth])
  .server(async ({ next, context }) => {
    const email = (context.claims as { email?: string })?.email;
    if (email !== ADMIN_EMAIL) {
      throw new Error("Forbidden: Admin access required");
    }
    return next({ context });
  });

function anonClient() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { persistSession: false, autoRefreshToken: false } },
  );
}

// ---- Public ----

export const listRoomsAndBlocks = createServerFn({ method: "GET" }).handler(async () => {
  const supabase = anonClient();
  const [roomsRes, blocksRes, seasonsRes] = await Promise.all([
    supabase.from("rooms").select("id, slug, name, price_cad, sort_order").order("sort_order"),
    supabase.from("room_blocks").select("id, room_id, start_date, end_date"),
    supabase
      .from("seasonal_rates")
      .select("id, name, start_date, end_date, adjustment_type, adjustment_value, created_at")
      .order("created_at", { ascending: false }),
  ]);
  if (roomsRes.error) throw new Error(roomsRes.error.message);
  if (blocksRes.error) throw new Error(blocksRes.error.message);
  if (seasonsRes.error) throw new Error(seasonsRes.error.message);
  return {
    rooms: roomsRes.data ?? [],
    blocks: blocksRes.data ?? [],
    seasons: seasonsRes.data ?? [],
  };
});

const submitSchema = z.object({
  room_id: z.string().uuid(),
  guest_name: z.string().trim().min(1).max(100),
  email: z.string().trim().email().max(255),
  phone: z.string().trim().min(1).max(30),
  check_in: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  check_out: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  guests: z.number().int().min(1).max(20),
  notes: z.string().max(1000).optional(),
  arrival_time: z.string().trim().max(50).optional(),
});

export const submitBookingRequest = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => submitSchema.parse(input))
  .handler(async ({ data }) => {
    if (new Date(data.check_out) <= new Date(data.check_in)) {
      throw new Error("Check-out must be after check-in");
    }
    const supabase = anonClient();
    const bookingReference = crypto.randomUUID().replace(/-/g, "").slice(0, 8).toUpperCase();

    // Conflict check
    const { data: blocks, error: blocksErr } = await supabase
      .from("room_blocks")
      .select("id")
      .eq("room_id", data.room_id)
      .lt("start_date", data.check_out)
      .gt("end_date", data.check_in);
    if (blocksErr) throw new Error(blocksErr.message);
    if (blocks && blocks.length > 0) {
      return { ok: false as const, reason: "unavailable" as const };
    }

    const { error } = await supabase.from("bookings").insert({
      room_id: data.room_id,
      guest_name: data.guest_name,
      email: data.email,
      phone: data.phone,
      check_in: data.check_in,
      check_out: data.check_out,
      guests: data.guests,
      notes: data.notes ?? null,
    });
    if (error) throw new Error(error.message);

    // Notify property manager (do not fail the booking on email errors)
    try {
      const { data: room } = await supabase
        .from("rooms")
        .select("name")
        .eq("id", data.room_id)
        .maybeSingle();
      const roomName = room?.name ?? "Unknown room";

      // Build printable PDF (optional — never block the email on PDF failure)
      let attachments: Array<{ filename: string; content: string; contentType?: string }> | undefined;
      try {
        const pdfBytes = await renderBookingPdf({
          shortId: bookingReference,
          submittedAt: new Date(),
          roomName,
          guestName: data.guest_name,
          email: data.email,
          phone: data.phone,
          checkIn: data.check_in,
          checkOut: data.check_out,
          guests: data.guests,
          notes: data.notes ?? null,
          arrivalTime: data.arrival_time ?? null,
        });
        const base64 = Buffer.from(pdfBytes).toString("base64");
        attachments = [{
          filename: `booking-${bookingReference}-${data.check_in}.pdf`,
          content: base64,
          contentType: "application/pdf",
        }];
      } catch (pdfErr) {
        console.error("Booking PDF generation failed:", pdfErr);
      }

      await sendEmail({
        to: "infomalakbnb@gmail.com",
        cc: "lionroor@outlook.com",
        bcc: "clients@vortio.com",
        replyTo: data.email,
        subject: `New booking request: ${roomName} (${data.check_in} → ${data.check_out})`,
        html: renderBookingRequestEmail({
          roomName,
          guestName: data.guest_name,
          email: data.email,
          phone: data.phone,
          checkIn: data.check_in,
          checkOut: data.check_out,
          guests: data.guests,
          notes: data.notes ?? null,
          arrivalTime: data.arrival_time ?? null,
        }),
        attachments,
      });
    } catch (e) {
      console.error("Booking notification email failed:", e);
    }

    return { ok: true as const };
  });

const contactSchema = z.object({
  name: z.string().trim().min(1).max(100),
  email: z.string().trim().email().max(255),
  message: z.string().trim().min(1).max(2000),
});

export const submitContactMessage = createServerFn({ method: "POST" })
  .inputValidator((input) => contactSchema.parse(input))
  .handler(async ({ data }) => {
    try {
      await sendEmail({
        to: "infomalakbnb@gmail.com",
        cc: "lionroor@outlook.com",
        bcc: "clients@vortio.com",
        replyTo: data.email,
        subject: `New contact message: ${data.name}`,
        html: renderContactMessageEmail({
          name: data.name,
          email: data.email,
          message: data.message,
        }),
      });
    } catch (e) {
      console.error("Contact message email failed:", e);
      throw new Error("Failed to send message. Please try again later.");
    }
    return { ok: true as const };
  });

// ---- Admin ----


export const adminListBookings = createServerFn({ method: "GET" })
  .middleware([requireAdmin])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("bookings")
      .select("id, room_id, guest_name, email, phone, check_in, check_out, guests, notes, status, created_at, rooms(name)")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return { bookings: data ?? [] };
  });

export const adminUpdateBookingStatus = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .inputValidator((input: unknown) =>
    z.object({
      id: z.string().uuid(),
      status: z.enum(["approved", "declined", "pending"]),
    }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { data: booking, error: fetchErr } = await context.supabase
      .from("bookings")
      .select("id, room_id, check_in, check_out, status")
      .eq("id", data.id)
      .maybeSingle();
    if (fetchErr) throw new Error(fetchErr.message);
    if (!booking) throw new Error("Booking not found");

    const { error: updErr } = await context.supabase
      .from("bookings")
      .update({ status: data.status })
      .eq("id", data.id);
    if (updErr) throw new Error(updErr.message);

    if (data.status === "approved") {
      // Add a block for these dates linked to this booking (if not already)
      const { data: existing } = await context.supabase
        .from("room_blocks")
        .select("id")
        .eq("booking_id", data.id)
        .maybeSingle();
      if (!existing) {
        const { error: blockErr } = await context.supabase.from("room_blocks").insert({
          room_id: booking.room_id,
          start_date: booking.check_in,
          end_date: booking.check_out,
          booking_id: data.id,
          reason: "Approved booking",
        });
        if (blockErr) throw new Error(blockErr.message);
      }
    } else {
      // Remove any auto-block tied to this booking
      await context.supabase.from("room_blocks").delete().eq("booking_id", data.id);
    }

    return { ok: true as const };
  });

export const adminCreateBlock = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .inputValidator((input: unknown) =>
    z.object({
      room_id: z.string().uuid(),
      start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      end_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      reason: z.string().max(200).optional(),
    }).parse(input),
  )
  .handler(async ({ data, context }) => {
    if (new Date(data.end_date) <= new Date(data.start_date)) {
      throw new Error("End date must be after start date");
    }
    const { error } = await context.supabase.from("room_blocks").insert({
      room_id: data.room_id,
      start_date: data.start_date,
      end_date: data.end_date,
      reason: data.reason ?? "Manual block",
    });
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });

export const adminDeleteBlock = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .inputValidator((input: unknown) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("room_blocks").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });

// ---- Admin: pricing ----

export const adminUpdateRoomPrice = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .inputValidator((input: unknown) =>
    z.object({
      id: z.string().uuid(),
      price_cad: z.number().min(0).max(100000),
    }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("rooms")
      .update({ price_cad: data.price_cad })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });

export const adminBulkAdjustPrices = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .inputValidator((input: unknown) =>
    z.object({
      type: z.enum(["percent", "flat"]),
      value: z.number().min(-100000).max(100000),
    }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { data: rooms, error: fetchErr } = await context.supabase
      .from("rooms")
      .select("id, price_cad");
    if (fetchErr) throw new Error(fetchErr.message);
    for (const r of rooms ?? []) {
      const base = Number(r.price_cad);
      const next =
        data.type === "percent"
          ? base * (1 + data.value / 100)
          : base + data.value;
      const rounded = Math.max(0, Math.round(next));
      const { error: updErr } = await context.supabase
        .from("rooms")
        .update({ price_cad: rounded })
        .eq("id", r.id);
      if (updErr) throw new Error(updErr.message);
    }
    return { ok: true as const, count: rooms?.length ?? 0 };
  });

const seasonSchema = z.object({
  name: z.string().trim().min(1).max(100),
  start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  end_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  adjustment_type: z.enum(["percent", "flat"]),
  adjustment_value: z.number().min(-100000).max(100000),
});

export const adminListSeasons = createServerFn({ method: "GET" })
  .middleware([requireAdmin])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("seasonal_rates")
      .select("id, name, start_date, end_date, adjustment_type, adjustment_value, created_at")
      .order("start_date", { ascending: true });
    if (error) throw new Error(error.message);
    return { seasons: data ?? [] };
  });

export const adminCreateSeason = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .inputValidator((input: unknown) => seasonSchema.parse(input))
  .handler(async ({ data, context }) => {
    if (new Date(data.end_date) < new Date(data.start_date)) {
      throw new Error("End date must be on or after start date");
    }
    const { error } = await context.supabase.from("seasonal_rates").insert(data);
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });

export const adminUpdateSeason = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .inputValidator((input: unknown) =>
    seasonSchema.extend({ id: z.string().uuid() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    if (new Date(data.end_date) < new Date(data.start_date)) {
      throw new Error("End date must be on or after start date");
    }
    const { id, ...rest } = data;
    const { error } = await context.supabase
      .from("seasonal_rates")
      .update(rest)
      .eq("id", id);
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });

export const adminDeleteSeason = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .inputValidator((input: unknown) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("seasonal_rates")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });

